import { Card, Space, Typography, theme, Skeleton, Layout } from "antd";
import { AnnotatedDeepLink, DigestV2State } from "pages/api/deep-links/types";
import { useCardRegistry } from "./CardRegistryContext";
import { useTopicResults } from "../hooks/useTopicResults";
import { useComposedPrompt } from "../hooks/useComposedPrompt";
import { CardControls } from "./components/CardControls";
import React, { memo } from "react";
import { useRouter } from "next/router";
import { createCompositeCard } from "./composite/CompositeCard";
import styles from "../components/topicfeed.module.css";
import { PromptInstructionsContext } from "../components/PromptInstructionsContext";

export interface CardRendererProps {
  link: AnnotatedDeepLink<DigestV2State>;
  size?: "mini" | "expanded";
  cardType?: "feed" | "topic";
  onDataChange?: (data: any, isLoading: boolean) => void;
  showErrorCards?: boolean;
  onClick?: () => void;
  onErrored?: () => void;
}

const ErrorCard = memo(
  ({
    name,
    description,
    error,
    link,
  }: {
    name: string;
    description: string;
    error: string;
    link: AnnotatedDeepLink<DigestV2State>;
  }) => {
    const { token } = theme.useToken();
    return (
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {name}
            </Typography.Title>
            <Typography.Text type="secondary">{description}</Typography.Text>
          </div>
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              background: token.colorFillQuaternary,
              borderRadius: token.borderRadiusLG,
            }}
          >
            <Typography.Text type="secondary">{error}</Typography.Text>
          </div>
        </Space>
        <Layout.Footer>
          <CardControls
            link={link}
            hideFavorite={true}
            hideCopyLink={false}
            tags={[]}
          />
        </Layout.Footer>
      </Card>
    );
  }
);

ErrorCard.displayName = "ErrorCard";

export const CardRenderer = memo(function CardRenderer({
  link,
  size = "mini",
  cardType = "topic",
  onDataChange,
  showErrorCards = false,
  onClick,
  onErrored,
}: CardRendererProps): React.ReactElement | null {
  const router = useRouter();
  const orgId = router.query.orgId as string;
  const { token } = theme.useToken();
  const state = link.json as DigestV2State;

  let topicCard;
  // Get card configuration.. need a fallback using blocks, because some old cards state may still not have an id
  if (
    state.topicCardConfig?.id === "composite" ||
    (state.topicCardConfig?.blocks && state.topicCardConfig?.blocks.length > 0)
  ) {
    const blocks = state.topicCardConfig?.blocks ?? [];
    topicCard = createCompositeCard(Array.isArray(blocks) ? blocks : []);
  }

  // Use prompt instructions from context
  const promptInstructions = React.useContext(PromptInstructionsContext);
  const promptKeys = state.globalKeys;

  const composedPrompt = useComposedPrompt(
    (typeof topicCard?.recipe === "function"
      ? topicCard.recipe()
      : topicCard?.recipe) || {
      id: "default",
      name: "Default Recipe",
      format: { type: "json", fields: [], constraints: [] },
      validationRules: [],
      globalKey: [],
      prefix: "",
    },
    state.prompt,
    promptInstructions.filter((prefix) =>
      promptKeys?.includes(prefix.global_key)
    )
  );

  const {
    data: queryResult,
    isLoading,
    error,
  } = useTopicResults({
    orgId,
    state,
    prompt: composedPrompt,
    options: {
      cacheKey: `card-${topicCard?.name}`,
      staleTime: 10_000,
    },
  });

  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(queryResult, isLoading);
    }
  }, [queryResult, isLoading, onDataChange, link.id]);

  if (!topicCard) {
    onErrored?.();
    return showErrorCards ? (
      <ErrorCard
        name={link.name}
        description={link.description || ""}
        link={link as AnnotatedDeepLink<DigestV2State>}
        error="No valid card configuration"
      />
    ) : null;
  }

  if (isLoading) {
    return (
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          width: "100%",
          height: "342px",
          margin: "0",
          display: "flex",
          flexDirection: "column",
          marginBottom: "16px",
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Skeleton
            active
            title={false}
            style={{ height: "100%", width: "100%" }}
            paragraph={{
              rows: 8,
              width: ["85%", "90%", "95%", "80%", "85%", "75%", "60%"],
            }}
          />
        </Space>
      </Card>
    );
  }

  if (error || !queryResult?.summary) {
    onErrored?.();
    return showErrorCards ? (
      <ErrorCard
        name={link.name}
        description={link.description || ""}
        link={link as AnnotatedDeepLink<DigestV2State>}
        error={`Error loading data: ${
          error instanceof Error ? error.message : "No data available"
        }`}
      />
    ) : null;
  }

  const processedRecords =
    (queryResult.details?.[0] as any)?.processedRecords || 0;

  if (processedRecords < 5) {
    onErrored?.();
    return showErrorCards ? (
      <ErrorCard
        name={link.name}
        description={link.description || ""}
        link={link as AnnotatedDeepLink<DigestV2State>}
        error={`Not enough data available. Found ${processedRecords} records, minimum required is 10.`}
      />
    ) : null;
  }
  try {
    const parsedData = topicCard.parser(queryResult.summary);
    if (parsedData?.error) {
      onErrored?.();
      return showErrorCards ? (
        <ErrorCard
          name={link.name}
          description={link.description || ""}
          link={link as AnnotatedDeepLink<DigestV2State>}
          error={`Error parsing data: ${parsedData.message}`}
        />
      ) : null;
    }
    let rendered;

    rendered = topicCard.renderer({
      data: { ...parsedData.data },
      link,
      size,
      processedRecords: (queryResult.details?.[0] as any)?.processedRecords,
      totalRecords: (queryResult.details?.[0] as any)?.totalRecords,
      onClick,
    });

    return cardType === "feed" ? (
      <Card
        className={`${styles.card} feed-card card`}
        bodyStyle={{ padding: 8 }}
      >
        {rendered}
      </Card>
    ) : (
      (rendered as React.ReactElement)
    );
  } catch (error) {
    console.error("Error rendering card", error);
    onErrored?.();
    return showErrorCards ? (
      <ErrorCard
        name={link.name}
        description={link.description || ""}
        link={link as AnnotatedDeepLink<DigestV2State>}
        error={"An unexpected error occurred while rendering the card"}
      />
    ) : null;
  }
});
